import {
    _forEach, _groupBy, _map, _forOwn,
} from '@samclick-lodash';
import { DEFAULT_FORMAT, formatDate } from '@/utils/date-utils';
import { toMoney } from '@/utils/common-utils';

export class BasicLineSetting {
    legend: string = ''

    color: string = ''

    key: string = ''

    isMoney: boolean = false

    valuePosition: string = 'left'

    borderDash: number[];

    borderWidth: number;

    constructor(legend: string = '', color: string = '', key: string = '', valuePosition: string = 'left', isMoney: boolean = false, borderDash: number[], borderWidth: number) {
        this.legend = legend;
        this.color = color;
        this.key = key;
        this.isMoney = isMoney;
        this.valuePosition = valuePosition;
        this.borderDash = borderDash;
        this.borderWidth = borderWidth;
    }
}

export class BasicLineConfig {
    constructor(groupBy: string = '', config: BasicLineSetting[] = [], dateFormat: string = DEFAULT_FORMAT) {
        this.groupBy = groupBy;
        this.dateFormat = dateFormat;
        this.config = config;
    }

    groupBy: string = ''

    dateFormat: string = '' // optional if groupBy is date only

    config: BasicLineSetting[] = []
}

function extractValues(tempData: any, option: BasicLineSetting) {
    let values: object[] = [];
    _forOwn(tempData, (value) => {
        values = [...values, ...value];
    });

    return _map(values, v => (option.isMoney ? toMoney(v[option.key], 2, false) : v[option.key]));
}

function extractKeys(tempData: any, config: BasicLineConfig) {
    const keys = Object.keys(tempData);
    if (config.groupBy === 'date') {
        const dateKeys: string[] = [];
        _forEach(keys, (key) => {
            dateKeys.push(formatDate(key, config.dateFormat));
        });

        return dateKeys;
    }
    return keys;
}


function buildDataSets(config: BasicLineConfig, data: object[]) {
    const tempData = _groupBy(data, config.groupBy);
    let datasets: object[] = [];
    _forEach(config.config, (option) => {
        const sett = {
            label: option.legend,
            fill: false,
            backgroundColor: option.color,
            borderColor: option.color,
            data: extractValues(tempData, option),
            yAxisID: option.valuePosition,
            pointStyle: 'line',
            borderDash: option.borderDash,
        };

        datasets = [...datasets, sett];
    });

    return {
        labels: extractKeys(tempData, config),
        datasets,
    };
}

export function generateBasicLine(config: BasicLineConfig, data: object[] = [], parentProps: object) {
    return {
        chartData: buildDataSets(config, data),
        chartOptions: {
            responsive: true,
            maintainAspectRatio: false,
            title: {
                display: true,
                text: parentProps.title,
            },
            legend: {
                labels: {
                    usePointStyle: true,
                },
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label(tooltipItem, data) {
                        let tooltipValue = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
                        const tootTiplabel = data.datasets[tooltipItem.datasetIndex].label;

                        if (tooltipValue.toString().indexOf('.') !== -1) {
                            tooltipValue = toMoney(tooltipValue);
                        } else {
                            tooltipValue = tooltipValue.toString();
                        }

                        return `${tootTiplabel} : ${tooltipValue}`;
                    },
                },
            },
            hover: {
                mode: 'nearest',
                intersect: true,
            },
            scales: {
                yAxes: [{
                    type: 'linear',
                    display: true,
                    position: 'left',
                    id: 'left',
                    ticks: {
                        beginAtZero: true,
                        userCallback(value, index, values) {
                            // Convert the number to a string and splite the string every 3 charaters from the end
                            value = value.toString();
                            value = value.split(/(?=(?:...)*$)/);
                            value = value.join(',');
                            return value;
                        },
                    },
                }, {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    id: 'right',
                    ticks: {
                        callback(value, index, values) {
                            return `${Math.round(value * 100) / 100}%`;
                        },
                        beginAtZero: true,
                    },
                    gridLines: {
                        drawOnChartArea: false,
                    },
                }],
            },
        },
    };
}
